// Half-circle meter used by the ORDER dashboard's FLO metrics (CHARGE / LOAD /
// FLOW). The arc is a risk ramp read left→right: green (fine) → yellow →
// orange → red (bad). Each metric converts its own value into that 0..1
// "badness" fraction, so one arc reads the same way for all three even though
// a high CHARGE is good and a high LOAD is not.
//
// The needle sits at the fraction, and the centre orb takes the colour of the
// band the needle is currently in — so the orb IS the reading, at a glance.
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import Svg, { Circle, G, Line, Path } from 'react-native-svg';
import { useTheme, makeThemedStyles, DISPLAY_FONT, type V10Palette } from '../v10/theme';

/** Ramp stops, in order. `upTo` is the exclusive top of that band's fraction. */
export interface GaugeBand {
  upTo: number;
  color: string;
}

/** The shared 4-stop ramp: gold when fine, amber (behind) then red (lapsed)
 *  as the reading worsens. Callers place their own band edges on it. */
export function rampFor(t: V10Palette) {
  return { green: t.gold, yellow: t.goldBright, orange: t.behind, red: t.lapsed };
}
export function useRamp() {
  const { t } = useTheme();
  return rampFor(t);
}

export function bandColor(fraction: number, bands: GaugeBand[]): string {
  for (const b of bands) if (fraction < b.upTo) return b.color;
  return bands[bands.length - 1].color;
}

const SEGMENTS = 12; // tick blocks around the arc
const GAP_DEG = 2.4; // gap between blocks

export function Gauge({
  label,
  value,
  fraction,
  bands,
  size = 150,
}: {
  label: string;
  /** Already-formatted reading shown in the orb. */
  value: string;
  /** 0..1 position of the needle along the arc (0 = far left / best). */
  fraction: number;
  bands: GaugeBand[];
  size?: number;
}) {
  const { t } = useTheme();
  const styles = useStyles();
  const f = Math.max(0, Math.min(1, Number.isFinite(fraction) ? fraction : 0));
  const W = size;
  const R = W * 0.46;
  const CX = W / 2;
  const CY = R + W * 0.04;
  const STROKE = W * 0.1;
  // Small enough that the needle stays visible between orb rim and arc, and
  // that the orb's lower half still fits on the canvas.
  const ORB_R = R * 0.5;
  const H = CY + ORB_R + 4;

  const pt = (deg: number, r: number) => {
    const rad = (deg * Math.PI) / 180;
    return { x: CX + r * Math.cos(rad), y: CY + r * Math.sin(rad) };
  };

  // One block per segment, coloured by the band its midpoint falls in.
  const step = 180 / SEGMENTS;
  const blocks = Array.from({ length: SEGMENTS }, (_, i) => {
    const a0 = 180 + i * step + GAP_DEG / 2;
    const a1 = 180 + (i + 1) * step - GAP_DEG / 2;
    const mid = (i + 0.5) / SEGMENTS;
    const rMid = R - STROKE / 2;
    const p0 = pt(a0, rMid);
    const p1 = pt(a1, rMid);
    return {
      d: `M${p0.x.toFixed(2)},${p0.y.toFixed(2)} A${rMid.toFixed(2)},${rMid.toFixed(2)} 0 0 1 ${p1.x.toFixed(2)},${p1.y.toFixed(2)}`,
      color: bandColor(mid, bands),
    };
  });

  const needleDeg = 180 + f * 180;
  const tip = pt(needleDeg, R - STROKE - 3);
  const orb = bandColor(f, bands);

  return (
    <View style={[styles.wrap, { width: W }]}>
      <Svg width={W} height={H}>
        {blocks.map((b, i) => (
          <Path
            key={i}
            d={b.d}
            stroke={b.color}
            strokeWidth={STROKE}
            strokeLinecap="butt"
            fill="none"
          />
        ))}
        <Circle cx={CX} cy={CY} r={ORB_R} fill={orb} stroke={t.night} strokeWidth={1.5} />
        {/* Needle — drawn OVER the orb, so it reads from the hub all the way
            out to the arc instead of disappearing behind the reading. */}
        <G>
          <Line
            x1={CX}
            y1={CY}
            x2={tip.x}
            y2={tip.y}
            stroke={t.onGold}
            strokeWidth={W * 0.02}
            strokeLinecap="round"
          />
          <Circle cx={CX} cy={CY} r={W * 0.032} fill={t.onGold} />
        </G>
      </Svg>
      {/* Label + reading sit inside the orb. */}
      {/* Label + reading sit in the orb's LOWER half — the needle sweeps the
          upper half at every reading, and would otherwise strike the text. */}
      <View style={[styles.orbText, { top: CY + ORB_R * 0.02, width: W }]} pointerEvents="none">
        <Text style={[styles.orbLabel, { fontSize: Math.round(W * 0.082) }]}>{label}</Text>
        <Text style={[styles.orbValue, { fontSize: Math.round(W * 0.12) }]}>{value}</Text>
      </View>
    </View>
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
    wrap: { alignItems: 'center' },
    orbText: { position: 'absolute', alignItems: 'center' },
    orbLabel: {
      fontWeight: '900',
      letterSpacing: 0.6,
      color: t.onGold,
      fontFamily: DISPLAY_FONT,
    },
    orbValue: { fontWeight: '900', color: t.onGold, fontFamily: DISPLAY_FONT },
  }),
);

export default Gauge;
